import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Input from '@/components/common/Input';
import Button from '@/components/common/Button';
import useAppStore from '@/store/appStore';
import api from '@/utils/api';
import { Shield, Eye, EyeOff, ExternalLink } from 'lucide-react';

const makeProviderInfo = (redirectUri) => ({
  google: {
    name: 'Google',
    setupUrl: 'https://console.cloud.google.com/apis/credentials',
    steps: [
      'Go to Google Cloud Console',
      'Click "Create Credentials" → Select "OAuth client ID"',
      'Application type: "Web application"',
      `Add Authorized redirect URI: ${redirectUri}`,
      'Click Create → Copy Client ID and Client Secret'
    ]
  },
  microsoft: {
    name: 'Microsoft',
    setupUrl: 'https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade',
    steps: [
      'Go to Azure Portal → App registrations',
      'Click "New registration" → Enter name',
      `Under Redirect URI: select "Web", add ${redirectUri}`,
      'Click Register → Copy Application (client) ID',
      'Go to "API permissions" → Add: IMAP.AccessAsUser.All, offline_access',
      'Go to "Certificates & secrets" → New client secret → Copy secret value'
    ]
  }
});

const defaultRedirectUri = () => `${window.location.origin}/api/oauth2/callback`;

export default function OAuth2ConfigPrompt({ provider, onConfigured, onCancel }) {
  const { addNotification } = useAppStore();
  const queryClient = useQueryClient();
  const [showSecret, setShowSecret] = useState(false);
  const [formData, setFormData] = useState({
    clientId: '',
    clientSecret: '',
    redirectUri: defaultRedirectUri()
  });

  const redirectUri = formData.redirectUri || defaultRedirectUri();
  const providerInfo = makeProviderInfo(redirectUri)[provider];

  const updateMutation = useMutation({
    mutationFn: async (data) => {
      // Note: api interceptor already returns response.data
      return await api.put(`/settings/oauth2/${provider}`, data);
    },
    onSuccess: async () => {
      addNotification({
        type: 'success',
        title: 'OAuth2 Configured',
        message: `${providerInfo.name} OAuth2 configured successfully`
      });
      // Force immediate refetch to get updated configuration
      await queryClient.refetchQueries(['oauth2', 'providers']);
      onConfigured?.();
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Configuration Failed',
        message: error.response?.data?.error || error.message
      });
    }
  });

  const handleSave = () => {
    if (!formData.clientId || !formData.clientSecret) {
      addNotification({
        type: 'error',
        title: 'Missing Information',
        message: 'Please enter both Client ID and Client Secret'
      });
      return;
    }

    updateMutation.mutate(formData);
  };

  return (
    <div className="space-y-4 p-4 border-2 border-primary/30 rounded-lg bg-primary/5">
      <div className="flex items-start gap-3">
        <Shield className="w-5 h-5 text-primary mt-0.5" />
        <div className="flex-1">
          <h3 className="font-semibold text-sm">Configure {providerInfo.name} OAuth2</h3>
          <p className="text-xs text-muted-foreground mt-1">
            OAuth2 is not configured for {providerInfo.name}. Set it up to continue.
          </p>
        </div>
      </div>

      <div className="pl-8 space-y-4">
        {/* Setup Instructions */}
        <div className="p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded text-xs space-y-2">
          <div className="flex items-center justify-between">
            <strong className="text-blue-900 dark:text-blue-100">Setup Steps:</strong>
            <a
              href={providerInfo.setupUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-700 dark:text-blue-300 hover:underline flex items-center gap-1"
            >
              Open Console
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          {provider === 'google' && (
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded text-blue-900 dark:text-blue-100">
              <strong>Important:</strong> Choose <strong>"OAuth client ID"</strong> (not API key or Service account)
            </div>
          )}
          <ol className="list-decimal ml-4 space-y-1 text-blue-800 dark:text-blue-200">
            {providerInfo.steps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </div>

        {/* Configuration Form */}
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium mb-1">Client ID</label>
            <Input
              type="text"
              value={formData.clientId}
              onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
              placeholder="Enter OAuth2 Client ID"
              className="text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">Client Secret</label>
            <div className="flex gap-2">
              <Input
                type={showSecret ? 'text' : 'password'}
                value={formData.clientSecret}
                onChange={(e) => setFormData({ ...formData, clientSecret: e.target.value })}
                placeholder="Enter OAuth2 Client Secret"
                className="text-sm"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setShowSecret(!showSecret)}
              >
                {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">Redirect URI</label>
            <Input
              type="text"
              value={formData.redirectUri}
              onChange={(e) => setFormData({ ...formData, redirectUri: e.target.value })}
              className="text-sm font-mono"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Add this exact URL to your OAuth2 app's authorized redirect URIs
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            type="button"
            onClick={handleSave}
            loading={updateMutation.isPending}
            size="sm"
          >
            Save & Continue
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            size="sm"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
